import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

import { dashboardMetricValuesSchema, dashboardTimelinePointSchema } from '../../dashboard/model';

const optionalNumber = nullableOptional(z.number());
const optionalString = nullableOptional(z.string());

export const publisherDashboardInventoryRowSchema = z.looseObject({
  appId: optionalString,
  appBundleId: optionalString,
  appName: optionalString,
  domain: optionalString,
  adRequests: optionalNumber,
  pubAdImpressions: optionalNumber,
  adRequestsFilled: optionalNumber,
  pubRevenueShareIo: optionalNumber,
  pubRevenue: optionalNumber,
});

const publisherTotalsSchema = z.looseObject({
  adRequests: optionalNumber,
  pubAdImpressions: optionalNumber,
  pubRevenue: optionalNumber,
});

function normalizePublisherTotals(total: z.infer<typeof publisherTotalsSchema> | null | undefined) {
  return dashboardMetricValuesSchema.parse({
    adRequests: total?.adRequests,
    adImpressions: total?.pubAdImpressions,
    revenue: total?.pubRevenue,
  });
}

export const publisherDashboardOverviewSchema = z
  .looseObject({
    previousPeriodEnd: z.iso.datetime({ offset: true }).nullish(),
    total: publisherTotalsSchema.nullish(),
    totalPrevious: publisherTotalsSchema.nullish(),
    days: nullableArray(
      z.looseObject({
        day: z.string(),
        adRequests: optionalNumber,
        pubAdImpressions: optionalNumber,
        pubRevenue: optionalNumber,
      }),
    ),
  })
  .transform(({ previousPeriodEnd, total, totalPrevious, days }) => ({
    previousPeriodEnd: previousPeriodEnd ?? null,
    total: normalizePublisherTotals(total),
    totalPrevious: totalPrevious == null ? null : normalizePublisherTotals(totalPrevious),
    hours: [],
    totalsByDay: days.map((point) =>
      dashboardTimelinePointSchema.parse({
        day: point.day,
        adRequests: point.adRequests,
        adImpressions: point.pubAdImpressions,
        revenue: point.pubRevenue,
      }),
    ),
  }));

export const publisherDashboardInventorySchema = z
  .looseObject({
    apps: nullableArray(publisherDashboardInventoryRowSchema),
  })
  .transform(({ apps }) => apps);

export const publisherDashboardReportSchema = z.looseObject({}).transform((response) => ({
  overview: publisherDashboardOverviewSchema.parse(response),
  inventory: publisherDashboardInventorySchema.parse(response),
}));

export type PublisherDashboardOverview = z.infer<typeof publisherDashboardOverviewSchema>;
export type PublisherDashboardInventoryRow = z.infer<typeof publisherDashboardInventoryRowSchema>;
